import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { OnlineProjectsService, ProjectDto, TeamsService, UserDto, UsersService } from '@entscheidungsnavi/api-client';
import { FileExport } from '@entscheidungsnavi/widgets';
import { finalize, map } from 'rxjs';
import sanitize from 'sanitize-filename';
import { logError } from '@entscheidungsnavi/tools';
import { TeamProjectInfoWithRoleDto } from '@entscheidungsnavi/api-types';

type ProjectRow = (ProjectDto | TeamProjectInfoWithRoleDto) & { projectType: string };

@Component({
  selector: 'dt-user-projects',
  templateUrl: './user-projects.component.html',
  styleUrls: ['./user-projects.component.scss'],
  standalone: false,
})
export class UserProjectsComponent implements OnInit {
  @Input() user: UserDto;

  displayedColumns = ['projectType', 'name', 'updatedAt', 'createdAt', 'actions'];
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  isLoading = true;
  isDownloading = false;
  projects: MatTableDataSource<ProjectRow> = new MatTableDataSource();

  constructor(
    private usersService: UsersService,
    private onlineProjectsService: OnlineProjectsService,
    private teamService: TeamsService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.usersService.getUserProjects(this.user.id).subscribe({
      next: projects => {
        const allProjects = [...projects.onlineProjects, ...projects.teamProjects];
        const allProjectsInfo = allProjects.map(proj => {
          const projectType = proj instanceof TeamProjectInfoWithRoleDto ? 'team' : 'online';
          return { ...proj, projectType };
        });

        this.projects.data = allProjectsInfo;
        this.isLoading = false;
      },
      error: () => {
        this.snackBar.open($localize`Fehler beim Laden der Projektliste`, 'Ok');
      },
    });

    this.projects.sort = this.sort;
  }

  isTeamProject(project: ProjectRow): boolean {
    return project.projectType === 'team';
  }

  downloadProject(project: ProjectRow) {
    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;

    const fileName = (sanitize(project.name) || 'project') + '.json';

    const data$ = this.isTeamProject(project)
      ? this.teamService.getTeam(project.id).pipe(map(team => team.mainProjectData))
      : this.onlineProjectsService.getProject(project.id).pipe(map(result => result.data));

    data$
      .pipe(
        logError(),
        finalize(() => (this.isDownloading = false)),
      )
      .subscribe({
        next: data => FileExport.download(fileName, data),
        error: () => {
          this.snackBar.open($localize`Download fehlgeschlagen`, 'Ok');
        },
      });
  }
}
